import React, { useEffect, useState } from "react";
import { CheckCircle, WarningCircle, XCircle, CaretDown } from "@phosphor-icons/react";
import { t } from "../i18n";
import { shopifyOAuthStatus } from "../lib/api";

/** Compact, colored banner that shows in 2 sec whether OAuth/manual will work. */
export default function ShopifyHealthBanner({ lang }) {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    shopifyOAuthStatus()
      .then(setStatus)
      .catch(() => setStatus({ configured: false }));
  }, []);

  if (!status) return null;

  // Derive overall health state
  const problems = [];
  if (!status.token_encryption_ready) problems.push("TOKEN_ENCRYPTION_KEY missing");
  if (status.configured && status.host_matches === false) {
    problems.push(`APP_PUBLIC_URL mismatch (${status.app_public_url} vs ${status.detected_host})`);
  }
  if (!status.has_theme_scope) problems.push("Missing scope: read_themes");

  let state;
  if (!status.configured && problems.length === 0) state = "manual_only";
  else if (problems.length === 0) state = "ok";
  else if (status.configured && status.host_matches !== false && !status.has_theme_scope) state = "warn";
  else state = "broken";

  const palette = {
    ok:          { bg: "bg-emerald-50", border: "border-emerald-500", text: "text-emerald-900", icon: <CheckCircle size={14} weight="fill" className="text-emerald-700"/> },
    warn:        { bg: "bg-amber-50",   border: "border-amber-500",   text: "text-amber-900",   icon: <WarningCircle size={14} weight="fill" className="text-amber-700"/> },
    manual_only: { bg: "bg-blue-50",    border: "border-blue-500",    text: "text-blue-900",    icon: <WarningCircle size={14} weight="fill" className="text-blue-700"/> },
    broken:      { bg: "bg-red-50",     border: "border-red-500",     text: "text-red-900",     icon: <XCircle size={14} weight="fill" className="text-red-700"/> },
  }[state];

  const headline = {
    ok:          t(lang, "shopify_health_ok"),
    warn:        t(lang, "shopify_health_warn"),
    manual_only: t(lang, "shopify_health_manual_only"),
    broken:      t(lang, "shopify_health_broken"),
  }[state];

  return (
    <div className={`border ${palette.border} ${palette.bg}`} data-testid="shopify-health-banner">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center gap-2 px-3 py-2 ${palette.text} text-left hover:opacity-90 ma-trans`}
        data-testid="shopify-health-toggle"
      >
        {palette.icon}
        <span className="text-[11px] font-mono uppercase tracking-widest">
          {headline}
        </span>
        <CaretDown size={11} weight="bold" className={`ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 border-t border-current border-opacity-20" data-testid="shopify-health-details">
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-mono pt-2">
            <Row label={t(lang, "shopify_health_oauth_configured")} ok={status.configured} />
            <Row label={t(lang, "shopify_health_host_match")} ok={status.host_matches !== false}>
              {status.detected_host}
            </Row>
            <Row label={t(lang, "shopify_health_encryption")} ok={status.token_encryption_ready} />
            <Row label={t(lang, "shopify_health_scope_read_themes")} ok={status.has_theme_scope} />
            {status.redirect_uri && (
              <div className="md:col-span-2 mt-1">
                <span className="text-neutral-500 mr-2 uppercase tracking-widest text-[10px]">
                  {t(lang, "shopify_health_redirect_uri")}:
                </span>
                <span className="break-all" data-testid="shopify-health-redirect-uri">{status.redirect_uri}</span>
              </div>
            )}
          </dl>
          {state !== "ok" && (
            <p className="text-[11px] mt-2 leading-relaxed">
              {t(lang, `shopify_health_${state}_hint`)}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ label, ok, children }) {
  return (
    <div className="flex items-center gap-2">
      {ok ? (
        <CheckCircle size={12} weight="fill" className="text-emerald-700 shrink-0" />
      ) : (
        <XCircle size={12} weight="fill" className="text-red-700 shrink-0" />
      )}
      <span className="text-neutral-600 uppercase tracking-widest text-[10px]">{label}</span>
      {children && <span className="ml-auto text-neutral-700 truncate max-w-[60%]" title={String(children)}>{children}</span>}
    </div>
  );
}
